import i18n from '@/i18n'
import { sharedHelper } from '@/utils'

/**
 * The bones of a listing store module.
 *
 * ── Why this exists ─────────────────────────────────────────────────────
 * one_sales has ~35 of these modules and they are the same 140 lines each
 * time: a `list`/`details`/`status` state, five mutations, and an action
 * wrapper that alerts on failure and re-fetches the listing on success. Copying
 * it again for five more screens would be 700 lines in which the only
 * interesting parts are the service call and the row mapper — and the copies
 * there HAVE drifted: some read `error?.response?.data?.code`, some read
 * `error.response.data.code` and throw a second error inside the error handler
 * when the request never reached a server.
 *
 * So the shape is defined once and each module declares what differs.
 *
 * ── Why it lives here and not in modules/ ───────────────────────────────
 * `state/modules/index.js` registers EVERY .js file in that directory as a
 * Vuex module via require.context. A helpers file sitting there would be
 * registered as a namespaced module called `listingStore` with no state --
 * harmless, but a phantom in the devtools that somebody will eventually try to
 * use.
 *
 * ── The one behaviour to know about ─────────────────────────────────────
 * A failed action RESOLVES with undefined rather than rejecting, after
 * alerting. That is one_sales' convention and the pages depend on it:
 * `if (result) this.$router.push(...)` is how a form avoids navigating away
 * from an error. Changing it would make every form navigate on failure.
 */
export const LISTING_STATE = () => ({
  list: {
    data: [],
    pagination: { pageNumber: 0, pageSize: 0, lastPage: 0, tableRowsCounts: 0, total: 0 },
  },
  details: {},
  status: { list: true, detail: true, save: true },
})

export const REQUESTING = 'requesting'
export const SUCCESS = 'commonSuccess'
export const FAILURE = 'commonFailure'
export const LIST_SUCCESS = 'listSuccess'
export const DETAIL_SUCCESS = 'detailSuccess'

/**
 * @param {function} mapRow  one API row -> one table row. Called for the
 *                           listing AND the detail, so a form reads the same
 *                           field names the table does.
 */
export function listingMutations(mapRow) {
  return {
    [LIST_SUCCESS](state, result) {
      const rows = result?.data || []
      state.list.pagination = sharedHelper.apiPaginationAdapter(result?.pagination || {})
      // `success` false should not reach here — the action rejects first — but
      // a defensive map beats rendering raw snake_case keys if it ever does.
      state.list.data = result?.success === false ? [] : rows.map(mapRow)
    },
    [DETAIL_SUCCESS](state, result) {
      state.details = result?.data ? mapRow(result.data) : {}
    },
    [REQUESTING](state, type) {
      state.status[type] = false
    },
    [SUCCESS](state) {
      state.status = { list: true, detail: true, save: true }
    },
    [FAILURE](state) {
      state.status = { list: true, detail: true, save: true }
    },
  }
}

/** The failure path, written once. See the note above about resolving. */
export function alertAndClear(commit, error) {
  sharedHelper.alertErrorMessage(
    sharedHelper.getErrorMessage(error),
    error?.response?.data?.code
  )
  commit(FAILURE)
}

/**
 * A read that fills `list`.
 *
 * @param {function} call  (filter) => Promise of the API envelope
 */
export function listAction(call) {
  return function ({ commit }, { filter } = {}) {
    commit(REQUESTING, 'list')

    return call(filter)
      .then((result) => {
        commit(LIST_SUCCESS, result)
        commit(SUCCESS)
        return result
      })
      .catch((error) => alertAndClear(commit, error))
  }
}

/** A read that fills `details`. Returns the envelope so a form can seed itself
 *  from it without going through the store. */
export function detailAction(call) {
  return function ({ commit }, payload) {
    commit(REQUESTING, 'detail')

    return call(payload)
      .then((result) => {
        commit(DETAIL_SUCCESS, result)
        commit(SUCCESS)
        return result
      })
      .catch((error) => alertAndClear(commit, error))
  }
}

/**
 * A write, which alerts on success and re-fetches the listing.
 *
 * @param {function} call       (payload) => Promise
 * @param {function} successMsg (payload, result) => string
 * @param {string}   refetch    the action name to dispatch afterwards; pass
 *                              null for a write that should not re-fetch
 *                              (a form that is about to navigate away).
 */
export function writeAction(call, successMsg, refetch) {
  return function ({ commit, dispatch }, payload = {}) {
    commit(REQUESTING, 'save')

    return call(payload)
      .then((result) => {
        sharedHelper.alertSuccessMessage(successMsg(payload, result))
        commit(SUCCESS)

        // `filter` is how a listing's toggle asks to be re-read with the
        // search criteria it is currently showing. A form passes none and
        // navigates instead.
        if (refetch && payload.filter) {
          dispatch(refetch, { filter: payload.filter })
        }

        return result
      })
      .catch((error) => alertAndClear(commit, error))
  }
}

/** `label.createX` / `label.updateX` with a module name, as the pages read. */
export function createdMsg(moduleKey) {
  return () => i18n.t('label.createX', [i18n.t(moduleKey)])
}

export function updatedMsg(moduleKey) {
  return () => i18n.t('label.updateX', [i18n.t(moduleKey)])
}
